import {groupByUniqueKey, objectFromEntries} from '@benchling/js-utils';
import stringify from 'json-stable-stringify';

import {AssemblyLayoutValidator} from 'molbio/bulk-assembly/AssemblyLayoutValidator';
import {AssemblySelector} from 'molbio/bulk-assembly/AssemblySelector';
import {
  BulkAssemblyBinType,
  BulkAssemblyMethod,
  BulkAssemblyProcedure,
  FragmentProductionMethod,
  isBulkAssemblyFragmentBin,
  NormalizedConstruct,
  NormalizedFragment,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import BulkAssemblyPolymerData from 'molbio/bulk-assembly/BulkAssemblyPolymerData';
import {
  AssembledProteinConstruct,
  AssembledSequenceConstruct,
  AsyncBulkAssemblyValidationResults,
  BulkAssemblyConstructValidationResult,
  BulkAssemblyFragmentValidationResult,
  ProcessedAsyncBulkAssemblyValidationResults,
} from 'molbio/bulk-assembly/BulkAssemblyValidationConstants';
import {BAIdValue, BAValueType, isBANumericId, isEmptyBAValue} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {isProteinBasedAssembly, methodCanSpecifyEndpoints} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import {
  areEnzymesRequired,
  getPreferredRestrictionEnzymeId,
  getRestrictionEnzymeInfo,
} from 'molbio/bulk-assembly/util/EnzymeUtil';
import {validateEndpoints} from 'molbio/bulk-assembly/util/FragmentEndpointUtil';
import {LOADING} from 'util/Types';

export function constructPolymerIsSequence(
  polymer: AssembledSequenceConstruct | AssembledProteinConstruct
): polymer is AssembledSequenceConstruct {
  // Would be nice to use a purpose-built discriminator here.
  // eslint-disable-next-line benchlint/no-in-operator
  return 'createdSequenceId' in polymer;
}

/**
 * Cache for holding fragment and construct validation results at the granularity of individual
 * rows.
 *
 * In an ideal implementation, fragment and construct validation would be requested and cached
 * at a granular level and automatically batched into a single network call for efficiency. In
 * practice, that type of strategy is challenging for a few reasons:
 * - The rules of hooks make it hard to run a dynamic number of queries from a single component.
 * - Technically there are some ways that validation happens holistically for the entire
 *   assembly. We want to do an assembly-wide size check, and ideally any primer generation is
 *   optimized assembly-wide.
 *
 * Despite these limitations, there is real value in the validation being granular from the
 * user's perspective, since it's jarring to have unchanged rows go into a loading state. Rather
 * than relying on query-level caching provided by Apollo, we store validation results in our own
 * cache, keyed by a json-stable-stringified representation of the validation inputs for that row.
 * This cache is only ever accessed when in a loading state; we switch fully to Apollo results
 * once validation finishes.
 */
export interface BulkAssemblyValidationCache {
  fragmentResultsByKey: Map<string, BulkAssemblyFragmentValidationResult>;
  constructResultsByKey: Map<string, BulkAssemblyConstructValidationResult>;
}

export function makeBulkAssemblyValidationCache(): BulkAssemblyValidationCache {
  return {
    fragmentResultsByKey: new Map(),
    constructResultsByKey: new Map(),
  };
}

function getFragmentParams(
  normalizedFragment: NormalizedFragment,
  procedureJson: BulkAssemblyProcedure
): NormalizedFragment & {fragmentProductionMethod?: FragmentProductionMethod} {
  const assemblySelector = new AssemblySelector(procedureJson);
  return {
    ...normalizedFragment,
    fragmentProductionMethod:
      assemblySelector.getBinForFragment(normalizedFragment)?.fragmentProductionMethod,
  };
}

/**
 * Fragment errors are keyed by the fragment params and the method params.
 */
function fragmentCacheKey(
  normalizedFragment: NormalizedFragment,
  procedureJson: BulkAssemblyProcedure
): string {
  return stringify({
    fragment: getFragmentParams(normalizedFragment, procedureJson),
    params: procedureJson.method,
  });
}

/**
 * Construct errors are keyed by the construct input and all referenced fragment inputs.
 */
function constructCacheKey(
  normalizedConstruct: NormalizedConstruct,
  normalizedFragmentsByCid: {[fragmentCid: string]: NormalizedFragment},
  procedureJson: BulkAssemblyProcedure
): string {
  return stringify({
    fragments: Object.values(normalizedConstruct.constructFragmentsByBinId)
      .map((constructFragment) => constructFragment.fragment)
      .filter((constructFragmentReference) => constructFragmentReference.type === BAValueType.ID)
      .map((constructFragmentReference: BAIdValue) =>
        getFragmentParams(normalizedFragmentsByCid[constructFragmentReference.id], procedureJson)
      ),
    construct: normalizedConstruct,
    params: procedureJson.method,
    spacers: Object.values(procedureJson.binsByCid).filter(
      (bin) => bin.binType === BulkAssemblyBinType.SPACER
    ),
    topology: procedureJson.topology,
  });
}

/**
 * Prepare the arguments for server-side validation, determining which fragments and constructs
 * are eligible for validation. If no fragments (implicitly, also no constructs) are eligible for validation,
 * return null, meaning that we'll skip the server call.
 */
export function shouldSkipBulkAssemblyValidation(
  procedure: BulkAssemblyProcedure,
  bulkAssemblyPolymerData: BulkAssemblyPolymerData
): boolean {
  if (!procedure) {
    return false;
  }
  const assemblyLayoutValidator = new AssemblyLayoutValidator(procedure);
  const assemblySelector = new AssemblySelector(procedure);
  const fullySpecifiedFragments = procedure.fragments.filter((fragment) => {
    const {
      sequence,
      sequenceVersionDbId,
      protein,
      proteinVersionDbId,
      bin,
      start,
      end,
      orientation,
      preferredPrimer5,
      preferredPrimer3,
      restrictionEnzyme5,
      restrictionEnzyme3,
    } = fragment;
    let polymer;
    let polymerVersionDbId;
    if (isProteinBasedAssembly(procedure.method)) {
      polymer = protein;
      polymerVersionDbId = proteinVersionDbId;
    } else {
      polymer = sequence;
      polymerVersionDbId = sequenceVersionDbId;
    }
    if (
      polymer.type === BAValueType.ID &&
      polymerVersionDbId != null &&
      bin.type === BAValueType.ID &&
      (start.type === BAValueType.NUMBER || !methodCanSpecifyEndpoints(BulkAssemblyMethod.HOMOLOGY)) &&
      (end.type === BAValueType.NUMBER || !methodCanSpecifyEndpoints(BulkAssemblyMethod.HOMOLOGY)) &&
      orientation.type === BAValueType.ORIENTATION &&
      (preferredPrimer5.type === BAValueType.ID || isEmptyBAValue(preferredPrimer5)) &&
      (preferredPrimer3.type === BAValueType.ID || isEmptyBAValue(preferredPrimer3))
    ) {
      const polymerData = bulkAssemblyPolymerData.getBasicPolymerInfo(polymer.id, polymerVersionDbId);
      if (polymerData == null) {
        return false;
      }
      const restrictionEnzymeId5 = isBANumericId(restrictionEnzyme5) ? restrictionEnzyme5.id : null;
      const restrictionEnzymeId3 = isBANumericId(restrictionEnzyme3) ? restrictionEnzyme3.id : null;
      const restrictionEnzymeInfo5 = getRestrictionEnzymeInfo(
        restrictionEnzymeId5,
        polymerData.residues,
        polymerData.circular
      );
      const restrictionEnzymeInfo3 = getRestrictionEnzymeInfo(
        restrictionEnzymeId3,
        polymerData.residues,
        polymerData.circular
      );
      const binObject = procedure.binsByCid[bin.id];
      const preferredRestrictionEnzymeId = getPreferredRestrictionEnzymeId(assemblySelector.getMethod());
      const areRestrictionEnzymesRequired = areEnzymesRequired(
        assemblySelector.getConstructFragmentIndex(fragment),
        getRestrictionEnzymeInfo(preferredRestrictionEnzymeId, polymerData.residues, polymerData.circular),
        assemblySelector.getSequenceTopology(),
        assemblySelector.getNumFragmentBins(),
        assemblySelector.getFragmentProductionMethodForBin(fragment.bin)
      );
      const {startErrorMessage, endErrorMessage} = validateEndpoints(
        {start, end},
        polymerData.residues.length,
        polymerData.startIndex,
        orientation.value,
        restrictionEnzymeInfo5,
        restrictionEnzymeInfo3,
        methodCanSpecifyEndpoints(procedure.method.type),
        polymerData.circular,
        areRestrictionEnzymesRequired
      );
      if (
        startErrorMessage == null &&
        endErrorMessage == null &&
        binObject != null &&
        isBulkAssemblyFragmentBin(binObject) &&
        binObject.fragmentProductionMethod != null
      ) {
        return true;
      }
    }
    return false;
  });
  // Don't bother running validation if there are no fragments to validate.
  if (fullySpecifiedFragments.length === 0) {
    return true;
  }

  const layoutValidation = assemblyLayoutValidator.getAssemblyLayoutValidationErrors();
  if (
    !Object.values(layoutValidation.errorsByBin).every((errors) => errors.size === 0) ||
    layoutValidation.otherErrors.length !== 0
  ) {
    return true;
  }

  return false;
}

function populateValidationCache(
  procedureJson: BulkAssemblyProcedure,
  queryResults: AsyncBulkAssemblyValidationResults,
  cache: BulkAssemblyValidationCache
): void {
  const normalizedFragmentsByCid = groupByUniqueKey(
    procedureJson.fragments,
    (normalizedFragment) => normalizedFragment.cid
  );
  const normalizedConstructsByCid = groupByUniqueKey(
    procedureJson.constructs,
    (normalizedConstruct) => normalizedConstruct.cid
  );
  for (const fragmentResult of queryResults.fragmentValidationResults) {
    const normalizedFragment = normalizedFragmentsByCid[fragmentResult.fragmentId];
    if (normalizedFragment) {
      const cacheKey = fragmentCacheKey(normalizedFragment, procedureJson);
      cache.fragmentResultsByKey.set(cacheKey, fragmentResult);
    }
  }
  for (const constructResult of queryResults.constructValidationResults) {
    const normalizedConstruct = normalizedConstructsByCid[constructResult.constructId];
    if (normalizedConstruct) {
      const cacheKey = constructCacheKey(normalizedConstruct, normalizedFragmentsByCid, procedureJson);
      cache.constructResultsByKey.set(cacheKey, constructResult);
    }
  }
}

/**
 * Use the server-side validation response and inputs to create a mapping that makes it easy to
 * determine the validation status of each fragment and construct. For each item, it might have
 * validation results, or validation might be in progress, or it may not be validating at all.
 */
export function prepareValidationResults(
  // Procedure JSON, or null if no validation was performed.
  procedureJson: BulkAssemblyProcedure | null,
  // Results value if present, null if loading or skipped.
  queryResults: AsyncBulkAssemblyValidationResults | null,
  cache: BulkAssemblyValidationCache
): ProcessedAsyncBulkAssemblyValidationResults {
  if (queryResults && procedureJson) {
    populateValidationCache(procedureJson, queryResults, cache);
    return {
      assemblyResult: queryResults.assemblyValidationResult,
      fragmentResultsByCid: groupByUniqueKey(
        queryResults.fragmentValidationResults,
        (result) => result.fragmentId
      ),
      constructResultsByCid: groupByUniqueKey(
        queryResults.constructValidationResults,
        (result) => result.constructId
      ),
    };
  } else if (procedureJson) {
    const fragmentInputsByCid = groupByUniqueKey(
      procedureJson.fragments,
      (normalizedFragment) => normalizedFragment.cid
    );

    // While in a loading state, check if we have previous fragment or construct results in our own
    // cache and show them if so.
    return {
      assemblyResult: LOADING,
      fragmentResultsByCid: objectFromEntries(
        procedureJson.fragments.map((normalizedFragment) => {
          const cacheKey = fragmentCacheKey(normalizedFragment, procedureJson);
          return [normalizedFragment.cid, cache.fragmentResultsByKey.get(cacheKey) ?? LOADING];
        })
      ),
      constructResultsByCid: objectFromEntries(
        procedureJson.constructs.map((normalizedConstruct) => {
          const cacheKey = constructCacheKey(normalizedConstruct, fragmentInputsByCid, procedureJson);
          return [normalizedConstruct.cid, cache.constructResultsByKey.get(cacheKey) ?? LOADING];
        })
      ),
    };
  } else {
    return {
      assemblyResult: null,
      fragmentResultsByCid: {},
      constructResultsByCid: {},
    };
  }
}
